import { useState, useEffect, useCallback, Fragment } from 'react';
import { Store, Search, Package, AlertTriangle, TrendingDown, Eye, BarChart3, RefreshCw, Download, ChevronRight, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import api, { inventoryAPI, storesAPI } from '@/services/api';
import { EXPIRY_FILTERS, expiryParams, exportRowsToCsv } from '@/utils/stockExport';

const StoreStocksPage = () => {
  const [overview, setOverview] = useState(null);
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState('');
  const [stockData, setStockData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stockLoading, setStockLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [groupBy, setGroupBy] = useState('product');
  const [batchSearch, setBatchSearch] = useState('');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [includeZero, setIncludeZero] = useState(false);
  const [expandedProduct, setExpandedProduct] = useState(null);
  const [expandedBatches, setExpandedBatches] = useState([]);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  const { toast } = useToast();

  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await inventoryAPI.getHQStoresStockOverview();
      setOverview(res.data);
      setStores(res.data?.stores || []);
      
      // Auto-select first store if available
      if (res.data?.stores?.length > 0 && !selectedStore) {
        setSelectedStore(res.data.stores[0].store_id);
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch stores overview', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [selectedStore, toast]);

  const fetchStoreStock = useCallback(async () => {
    if (!selectedStore) return;
    
    setStockLoading(true);
    try {
      const params = { limit: 500, group_by: groupBy };
      if (search) params.search = search;
      if (filterType !== 'all') params.filter_type = filterType;
      if (groupBy === 'batch') {
        if (batchSearch) params.batch_search = batchSearch;
        if (includeZero) params.include_zero = true;
        Object.assign(params, expiryParams(expiryFilter));
      }
      
      const res = await inventoryAPI.getHQStoreStockDetail(selectedStore, params);
      setStockData(res.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch store stock', variant: 'destructive' });
    } finally {
      setStockLoading(false);
    }
  }, [selectedStore, search, filterType, groupBy, batchSearch, expiryFilter, includeZero, toast]);

  const toggleProductBatches = async (item) => {
    if (expandedProduct === item.product_id) {
      setExpandedProduct(null);
      setExpandedBatches([]);
      return;
    }
    setExpandedProduct(item.product_id);
    setExpandedBatches([]);
    try {
      const res = await api.get(`/inventory/batches/${item.product_id}`, { params: { store_id: selectedStore } });
      setExpandedBatches(res.data || []);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch batches', variant: 'destructive' });
    }
  };

  const handleExport = () => {
    const rows = stockData?.items || [];
    if (!rows.length) {
      toast({ title: 'Nothing to export', description: 'No stock rows in the current view' });
      return;
    }
    const columns = groupBy === 'batch'
      ? [
          { label: 'Product', key: 'name' },
          { label: 'SKU', key: 'sku' },
          { label: 'Batch', key: 'batch_number' },
          { label: 'Expiry', key: 'expiry_date' },
          { label: 'Qty', key: 'quantity' },
          { label: 'Cost', key: 'cost_price' },
          { label: 'Stock Value', key: 'stock_value' }
        ]
      : [
          { label: 'Product', key: 'name' },
          { label: 'SKU', key: 'sku' },
          { label: 'Category', key: 'category_name' },
          { label: 'Total Qty', key: 'quantity' },
          { label: 'Batches', key: 'batch_count' },
          { label: 'Nearest Expiry', key: 'nearest_expiry' },
          { label: 'Stock Value', key: 'stock_value' }
        ];
    exportRowsToCsv(rows, columns, `store-stock-${groupBy}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    if (selectedStore) {
      fetchStoreStock();
    }
  }, [selectedStore, search, filterType, fetchStoreStock]);

  const getSelectedStoreInfo = () => {
    return stores.find(s => s.store_id === selectedStore);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="hq-store-stocks-page">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Store Stocks</h1>
          <p className="text-gray-500">Monitor inventory across all stores</p>
        </div>
        <Button onClick={fetchOverview} variant="outline" className="border-gray-300">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-emerald-600 uppercase tracking-wide">Total Stores</p>
                <p className="text-2xl font-bold text-emerald-700">{overview?.summary?.total_stores || 0}</p>
              </div>
              <Store className="w-8 h-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 uppercase tracking-wide">Total Products</p>
                <p className="text-2xl font-bold text-blue-700">{overview?.summary?.total_products_across_stores || 0}</p>
              </div>
              <Package className="w-8 h-8 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-violet-50 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 uppercase tracking-wide">Stock Value (at cost)</p>
                <p className="text-2xl font-bold text-purple-700">₹{(overview?.summary?.total_stock_value || 0).toLocaleString()}</p>
                <p className="text-xs text-purple-500">Retail ₹{(overview?.summary?.total_retail_value || 0).toLocaleString()}</p>
              </div>
              <BarChart3 className="w-8 h-8 text-purple-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-amber-600 uppercase tracking-wide">Low Stock Stores</p>
                <p className="text-2xl font-bold text-amber-700">{overview?.summary?.stores_with_low_stock || 0}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-red-50 to-rose-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-red-600 uppercase tracking-wide">Out of Stock Stores</p>
                <p className="text-2xl font-bold text-red-700">{overview?.summary?.stores_with_out_of_stock || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stores Overview */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-lg font-semibold text-gray-800">Stores Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Store</TableHead>
                  <TableHead className="text-right">Products</TableHead>
                  <TableHead className="text-right">Total Qty</TableHead>
                  <TableHead className="text-right">Stock Value</TableHead>
                  <TableHead className="text-center">Low Stock</TableHead>
                  <TableHead className="text-center">Out of Stock</TableHead>
                  <TableHead className="text-center">Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.map((store) => (
                  <TableRow 
                    key={store.store_id} 
                    className={selectedStore === store.store_id ? 'bg-emerald-50' : 'hover:bg-gray-50'}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Store className="w-4 h-4 text-emerald-500" />
                        <div>
                          <div className="font-medium text-gray-800">{store.store_name}</div>
                          <div className="text-xs text-gray-500">{store.store_code}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-medium">{store.total_products}</TableCell>
                    <TableCell className="text-right">{store.total_qty.toLocaleString()}</TableCell>
                    <TableCell className="text-right text-emerald-600 font-medium">₹{store.stock_value.toLocaleString()}</TableCell>
                    <TableCell className="text-center">
                      {store.low_stock_count > 0 ? (
                        <Badge variant="warning" className="bg-amber-100 text-amber-700">{store.low_stock_count}</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      {store.out_of_stock_count > 0 ? (
                        <Badge variant="destructive" className="bg-red-100 text-red-700">{store.out_of_stock_count}</Badge>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge className={store.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
                        {store.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => setSelectedStore(store.store_id)}
                        className={selectedStore === store.store_id ? 'text-emerald-600' : ''}
                        data-testid={`hq-store-select-${store.store_id}`}
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Store Stock Details */}
      {selectedStore && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold text-gray-800">
                Stock Details - {getSelectedStoreInfo()?.store_name}
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <Input
                    placeholder="Search products..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="pl-9 w-64 bg-white border-gray-300"
                  />
                </div>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-40 bg-white border-gray-300">
                    <SelectValue placeholder="Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Products</SelectItem>
                    <SelectItem value="low_stock">Low Stock</SelectItem>
                    <SelectItem value="out_of_stock">Out of Stock</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row lg:items-center gap-3 pt-3">
              <div className="inline-flex rounded-lg border border-gray-200 overflow-hidden" data-testid="hq-store-stock-groupby-switch">
                <button
                  type="button"
                  onClick={() => setGroupBy('product')}
                  className={`px-4 py-2 text-sm transition-colors ${groupBy === 'product' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="hq-store-stock-groupby-product-btn"
                >
                  Product-wise
                </button>
                <button
                  type="button"
                  onClick={() => setGroupBy('batch')}
                  className={`px-4 py-2 text-sm transition-colors ${groupBy === 'batch' ? 'bg-emerald-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  data-testid="hq-store-stock-groupby-batch-btn"
                >
                  Batch-wise
                </button>
              </div>

              {groupBy === 'batch' && (
                <div className="flex flex-wrap items-center gap-3" data-testid="hq-store-stock-batch-filters">
                  <Input
                    placeholder="Batch / lot number..."
                    value={batchSearch}
                    onChange={(e) => setBatchSearch(e.target.value)}
                    className="w-56 bg-white border-gray-300"
                    data-testid="hq-store-stock-batch-search-input"
                  />
                  <Select value={expiryFilter} onValueChange={setExpiryFilter}>
                    <SelectTrigger className="w-48 bg-white border-gray-300" data-testid="hq-store-stock-expiry-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {EXPIRY_FILTERS.map(f => (
                        <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={includeZero}
                      onChange={(e) => setIncludeZero(e.target.checked)}
                      data-testid="hq-store-stock-include-zero-toggle"
                    />
                    Show zero-qty batches
                  </label>
                </div>
              )}

              <div className="lg:ml-auto">
                <Button variant="outline" size="sm" onClick={handleExport} data-testid="hq-store-stock-export-btn">
                  <Download className="w-4 h-4 mr-2" /> Export
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {stockLoading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-500"></div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead>Product</TableHead>
                      <TableHead>SKU / Barcode</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>{groupBy === 'batch' ? 'Batch / Expiry' : 'Batches'}</TableHead>
                      <TableHead className="text-right">Qty</TableHead>
                      <TableHead className="text-right">Cost Price</TableHead>
                      <TableHead className="text-right">Selling Price</TableHead>
                      <TableHead className="text-right">Stock Value</TableHead>
                      <TableHead className="text-center">Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stockData?.items?.map((item, index) => (
                      <Fragment key={item.stock_id || `${item.product_id}-${index}`}>
                      <TableRow className="hover:bg-gray-50">
                        <TableCell>
                          <div className="font-medium text-gray-800">{item.name}</div>
                          {item.brand_name && <div className="text-xs text-gray-500">{item.brand_name}</div>}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{item.sku}</div>
                          {item.barcode && <div className="text-xs text-gray-400">{item.barcode}</div>}
                        </TableCell>
                        <TableCell className="text-gray-600">{item.category_name || '-'}</TableCell>
                        <TableCell className="text-gray-600 text-sm">
                          {groupBy === 'batch' ? (
                            <>
                              {item.batch_number || 'Default'}
                              {item.expiry_date && (
                                <p className="text-xs text-gray-400">Exp: {String(item.expiry_date).slice(0, 10)}</p>
                              )}
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => toggleProductBatches(item)}
                              className="flex items-center gap-1 text-emerald-700 hover:underline"
                              data-testid={`hq-store-stock-expand-batches-${index}`}
                            >
                              {expandedProduct === item.product_id ? (
                                <ChevronDown className="w-3 h-3" />
                              ) : (
                                <ChevronRight className="w-3 h-3" />
                              )}
                              {item.batch_count || 0} batch{(item.batch_count || 0) === 1 ? '' : 'es'}
                              {item.nearest_expiry && (
                                <span className="text-xs text-gray-400 ml-1">(exp {String(item.nearest_expiry).slice(0, 10)})</span>
                              )}
                            </button>
                          )}
                        </TableCell>
                        <TableCell className="text-right font-medium">{item.quantity}</TableCell>
                        <TableCell className="text-right text-gray-600">₹{item.cost_price?.toLocaleString()}</TableCell>
                        <TableCell className="text-right text-emerald-600 font-medium">₹{item.selling_price?.toLocaleString()}</TableCell>
                        <TableCell className="text-right">₹{item.stock_value?.toLocaleString()}</TableCell>
                        <TableCell className="text-center">
                          {item.quantity <= 0 ? (
                            <Badge className="bg-red-100 text-red-700">Out of Stock</Badge>
                          ) : item.quantity < 10 ? (
                            <Badge className="bg-amber-100 text-amber-700">Low Stock</Badge>
                          ) : (
                            <Badge className="bg-emerald-100 text-emerald-700">In Stock</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                      {groupBy === 'product' && expandedProduct === item.product_id && (
                        expandedBatches.length === 0 ? (
                          <TableRow className="bg-gray-50" data-testid={`hq-store-stock-batch-subrow-${index}`}>
                            <TableCell colSpan={9} className="text-sm text-gray-500 pl-8">No batches with stock</TableCell>
                          </TableRow>
                        ) : (
                          expandedBatches.map((b, bi) => (
                            <TableRow key={bi} className="bg-gray-50" data-testid={bi === 0 ? `hq-store-stock-batch-subrow-${index}` : undefined}>
                              <TableCell className="pl-8 text-xs text-gray-500">Batch</TableCell>
                              <TableCell />
                              <TableCell />
                              <TableCell className="text-sm font-medium text-gray-800">
                                {b.batch_number}
                                {b.expiry_date && (
                                  <p className="text-xs text-gray-400">Exp: {String(b.expiry_date).slice(0, 10)}</p>
                                )}
                              </TableCell>
                              <TableCell className="text-right text-sm font-semibold">{b.quantity}</TableCell>
                              <TableCell />
                              <TableCell />
                              <TableCell />
                              <TableCell />
                            </TableRow>
                          ))
                        )
                      )}
                      </Fragment>
                    ))}
                    {(!stockData?.items || stockData.items.length === 0) && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center text-gray-500 py-8">
                          No stock data found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}
            {stockData?.total > 0 && (
              <div className="mt-4 text-sm text-gray-500 text-right">
                Showing {stockData.items?.length || 0} of {stockData.total} products
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default StoreStocksPage;
